import { createAsyncThunk } from "@reduxjs/toolkit";
import { checkStatus, AuthError, composeAuth } from "../../helpers/utils";
import { logoutUser } from "./account";

export const addReport = createAsyncThunk(
  "reports/addReport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.body),
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const addTagReport = createAsyncThunk(
  "reports/addTagReport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/tag`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.body),
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getReportById = createAsyncThunk(
  "reports/getReportById",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/${payload?.id}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getAllReportsByInstitute = createAsyncThunk(
  "reports/getAllReportsByInstitute",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/institution/${payload?.institutionId}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);


export const getReportByModuleAndModuleId = createAsyncThunk(
  "reports/getReportByModuleAndModuleId",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/module/${payload?.id}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const createReportBatch = createAsyncThunk(
  "reports/createReportBatch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      let goalIds = payload?.body?.goal_ids || [];

      if (payload?.selection?.allMatching) {
        const excludedIds = new Set(payload.selection.excludedGoalIds || []);
        const matchingIds = [];
        const pageSize = 100;
        let offset = 0;
        let total = 0;

        do {
          const params = new URLSearchParams({
            tag_ids: payload.body.tag_id,
            offset: String(offset),
            limit: String(pageSize),
          });
          const response = await fetch(
            `${process.env.REACT_APP_SERVER}/api/goals/institution/${payload.body.institutionId}/generate_ghost_goal/all?${params}`,
            {
              method: "PATCH",
              headers: {
                "Content-type": "application/json",
                Authorization,
              },
              body: JSON.stringify(payload.selection.filters),
              signal: thunkApi.signal,
            }
          );

          const result = checkStatus(await response.json());
          const goals = result?.data?.goals;
          total = Number(result?.data?.countAfterFilter);

          if (!Array.isArray(goals) || !Number.isSafeInteger(total) || total < 0) {
            throw new Error("Could not resolve the selected Logs");
          }
          if (!goals.length && offset < total) {
            throw new Error("The Log list changed while resolving the selection");
          }
          matchingIds.push(...goals.map((goal) => goal?._id).filter(Boolean));
          offset += goals.length;
        } while (offset < total);
        goalIds = [...new Set(matchingIds)].filter((id) => !excludedIds.has(id));

        if (goalIds.length !== payload.selection.expectedCount) {
          throw new Error("The Log list changed. Refresh it and select the Logs again.");
        }
      }

      if (!goalIds.length) {
        throw new Error("Select at least one Log");
      }

      const response = await fetch(`${process.env.REACT_APP_SERVER}/api/reports/batches`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
        body: JSON.stringify({
          ...payload.body,
          goal_ids: goalIds,
        }),
        signal: thunkApi.signal,
      });

      return checkStatus(await response.json());
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getReportBatchById = createAsyncThunk(
  "reports/getReportBatchById",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/batches/${payload?.id}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const getReportBatchesByInstitution = createAsyncThunk(
  "reports/getReportBatchesByInstitution",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const tagQuery = payload?.tag_id
        ? `?tag_id=${encodeURIComponent(payload?.tag_id)}`
        : "";
      const url =
        process.env.REACT_APP_SERVER +
        `/api/reports/batches/institution/${payload?.institutionId}${tagQuery}`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const regenerateReportBatch = createAsyncThunk(
  "reports/regenerateReportBatch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER + `/api/reports/batches/${payload?.id}/regenerate`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
        body: JSON.stringify(payload?.body),
      });

      const response = await res.json();
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const removeReportBatch = createAsyncThunk(
  "reports/removeReportBatch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/batches/${payload?.batchId}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });
      const raw = await res.text();
      let response = {};
      try {
        response = raw ? JSON.parse(raw) : {};
      } catch (e) {
        response = {};
      }
      if (!response.statusCode) {
        response.statusCode = res.status;
      }
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const removeReportFromBatch = createAsyncThunk(
  "reports/removeReportFromBatch",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url =
        process.env.REACT_APP_SERVER +
        `/api/reports/batches/${payload?.batchId}/reports/${payload?.reportId}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });
      const raw = await res.text();
      let response = {};
      try {
        response = raw ? JSON.parse(raw) : {};
      } catch (e) {
        response = {};
      }
      if (!response.statusCode) {
        response.statusCode = res.status;
      }
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);

export const removeReport = createAsyncThunk(
  "reports/removeReport",
  async (payload, thunkApi) => {
    try {
      const Authorization = composeAuth(payload?.jwt);
      const url = process.env.REACT_APP_SERVER + `/api/reports/${payload?.id}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-type": "application/json",
          Authorization,
        },
      });
      const raw = await res.text();
      let response = {};
      try {
        response = raw ? JSON.parse(raw) : {};
      } catch (e) {
        response = {};
      }
      if (!response.statusCode) {
        response.statusCode = res.status;
      }
      const validateData = checkStatus(response);
      return validateData;
    } catch (err) {
      if (err instanceof AuthError) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(err);
    }
  }
);
